import type { SxProps } from "@mui/material";
import { DateTime } from "luxon";
import type { Dispatch, SetStateAction } from "react";
import { v4 as uuidv4 } from "uuid";
import { INK, NEUTRAL, SHIFT_DAY, SHIFT_NIGHT_FALLBACK, SURFACE } from "../../../theme/tokens";
import { getWeekStart } from "../../../utils/dateUtils";
import {
	type ChangesSource,
	type ConsecutiveDaysRecurring,
	DAYS_PER_WEEK,
	type DailyRecurring,
	type DepartmentLocation,
	type RecurringShift,
	type WeeklyRecurring,
} from "../../../utils/models";
import { getDayNameByIndex } from "../../../utils/utilFunctions";
import type { SystemTab } from "../../UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";

/**
 * Date of `weekday` within the same week as `date`, where the week is anchored at
 * `weekStartDay` (0=Sun..6=Sat).
 *
 * A new recurring row arrives with `startDate` set to the displayed week's first day, which is
 * only the day it recurs on by coincidence. Every per-day create must pass through here, including
 * a create with a single weekday — skipping it is what wrote Monday rows with a Thursday
 * `start_date` and threw off the backend's biweek phase.
 */
export const weekdayInSameWeek = (date: DateTime, weekday: string, weekStartDay: number): string => {
	const target = WeekdaysToIndexMap[weekday.toLowerCase()];
	if (target === undefined) throw new Error(`weekdayInSameWeek: unknown weekday "${weekday}"`);
	const offsetWithinWeek = (target - weekStartDay + 7) % 7;
	return getWeekStart(date, weekStartDay).plus({ days: offsetWithinWeek }).toFormat("yyyy-MM-dd");
};

/**
 * One display line must map to exactly one logical row, and the two data models identify a row
 * differently: a per-day row is identified by the week it starts in, a legacy row by its own id.
 * `sharedParentId` picks which, so per-day rows starting in different weeks separate while a legacy
 * row's siblings stay together — and two legacy rows with matching parameters still stay apart.
 */
const mergeGroupKey = (shift: SingleRecurringShift, weekStartDay: number, sharedParentId: number | null): string => {
	const params = shift.recurrenceDetails;
	if (params.recurringType !== "Weekly") return "";
	const rowIdentity =
		sharedParentId === null ? getWeekStart(shift.originalStartDate ?? shift.startDate, weekStartDay).toFormat("yyyy-MM-dd") : `parent:${sharedParentId}`;
	return [
		shift.startTime,
		shift.endTime,
		shift.location ?? "",
		shift.endDate?.toFormat("yyyy-MM-dd") ?? "",
		shift.jobId ?? "",
		params.interval,
		rowIdentity,
	].join("|");
};

/**
 * Ids appearing more than once belong to a legacy multi-weekday row that `/recurring_shift/per_day`
 * expanded into virtual siblings, each walked forward to its own first firing date. Those dates can
 * straddle a week boundary, and the save path writes one weekday per physical id — so partitioning
 * them by week would let saving one line rewrite the shared parent with a subset of its weekdays.
 */
const findSharedParentIds = (shifts: WeeklyShifts): Set<number> => {
	const seen = new Set<number>();
	const shared = new Set<number>();
	for (const shift of shifts) {
		if (typeof shift.id !== "number") continue;
		if (seen.has(shift.id)) shared.add(shift.id);
		seen.add(shift.id);
	}
	return shared;
};

export const mergePerDayShifts = (shifts: WeeklyShifts, weekStartDay: number): WeeklyShifts => {
	const result: WeeklyShifts = [];
	const byKey = new Map<string, SingleRecurringShift>();
	const sharedParentIds = findSharedParentIds(shifts);
	for (const shift of shifts) {
		const params = shift.recurrenceDetails;
		const isMergeable =
			shift.status === SingleRecurringShiftStatus.EXISTING && params.recurringType === "Weekly" && params.weekdays.length === 1 && typeof shift.id === "number";
		if (!isMergeable) {
			result.push(shift);
			continue;
		}
		const weekday = (params as WeeklyRecurring).weekdays[0];
		const key = mergeGroupKey(shift, weekStartDay, sharedParentIds.has(shift.id as number) ? (shift.id as number) : null);
		const meta: MergedRowMeta = {
			id: shift.id as number,
			startDate: (shift.originalStartDate ?? shift.startDate).toFormat("yyyy-MM-dd"),
			endDate: shift.endDate?.toFormat("yyyy-MM-dd") ?? null,
		};
		const existing = byKey.get(key);
		if (!existing) {
			byKey.set(key, {
				...shift,
				recurrenceDetails: { ...params, weekdays: [weekday] } as WeeklyRecurring,
				originalRowsByWeekday: { [weekday]: meta },
			});
			continue;
		}
		const existingWeekly = existing.recurrenceDetails as WeeklyRecurring;
		// `originalRowsByWeekday` is what the save path splits the row back apart by, so a weekday
		// can only appear once per group — a second row for the same weekday stays standalone
		// rather than overwriting the first one's DB id.
		if (existingWeekly.weekdays.includes(weekday)) {
			result.push({ ...shift, recurrenceDetails: { ...params, weekdays: [weekday] } as WeeklyRecurring, originalRowsByWeekday: { [weekday]: meta } });
			continue;
		}
		existing.recurrenceDetails = { ...existingWeekly, weekdays: [...existingWeekly.weekdays, weekday] };
		existing.originalRowsByWeekday = { ...(existing.originalRowsByWeekday ?? {}), [weekday]: meta };
		if (shift.startDate < existing.startDate) {
			existing.startDate = shift.startDate;
			existing.originalStartDate = shift.originalStartDate;
			existing.id = shift.id;
		}
	}
	for (const merged of byKey.values()) result.push(merged);
	return result;
};

/**
 * The weeksByIndexes grid is bucketed by week before merging, but a legacy multi-weekday row's
 * `/per_day` siblings can land in different buckets when their first-fire dates straddle a week
 * boundary. Merging each bucket in isolation would split one physical row across lines, and the
 * save path would then rewrite or delete that shared row with only a subset of its weekdays. So
 * legacy shared-parent siblings are pulled out of the per-bucket merge, merged as whole rows
 * across every bucket, and each placed back into the single bucket its start week belongs to.
 */
export const mergePerDayShiftsByWeek = (buckets: Array<WeeklyShifts>, weekStartDay: number, weekIndexOf: (date: DateTime) => number): Array<WeeklyShifts> => {
	const sharedParentIds = findSharedParentIds(buckets.flat());
	const isSharedLegacy = (shift: SingleRecurringShift): boolean =>
		typeof shift.id === "number" &&
		shift.id > 0 &&
		shift.status === SingleRecurringShiftStatus.EXISTING &&
		shift.recurrenceDetails.recurringType === "Weekly" &&
		sharedParentIds.has(shift.id);
	const mergedByWeek = buckets.map((bucket) =>
		mergePerDayShifts(
			bucket.filter((shift) => !isSharedLegacy(shift)),
			weekStartDay,
		),
	);
	for (const legacyRow of mergePerDayShifts(buckets.flat().filter(isSharedLegacy), weekStartDay)) {
		const index = weekIndexOf(legacyRow.originalStartDate ?? legacyRow.startDate);
		if (index >= 0 && index < mergedByWeek.length) mergedByWeek[index].push(legacyRow);
	}
	return mergedByWeek;
};

export const recurringShiftMasterScheduleGeneralTypographySx = {
	fontWeight: 500,
	fontSize: "14px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: INK.SECONDARY,
} as const satisfies SxProps;

export const recurringShiftMasterScheduleRecurrenceSelectTypographySx = {
	...recurringShiftMasterScheduleGeneralTypographySx,
	fontSize: "12px",
} as const satisfies SxProps;

export const recurringShiftMasterScheduleShiftsListTypographySx = {
	fontWeight: 700,
	fontSize: "14px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: INK.SECONDARY,
} as const satisfies SxProps;

export enum RecurringShiftMasterScheduleType {
	CREATE = "create",
	EDIT = "edit",
}

export enum SingleRecurringShiftStatus {
	EXISTING = "Existing Shift",
	NEW = "New Shift",
	TO_DELETE = "Shift To Delete",
	EDITED = "Edited Shift",
}
export type MergedRowMeta = {
	id: number;
	startDate: string;
	endDate: string | null;
};

export type SingleRecurringShift = {
	recurrenceDetails: DailyRecurring | WeeklyRecurring | ConsecutiveDaysRecurring;
	/**
	 * HH:mm format
	 */
	startTime: string;
	/**
	 * HH:mm format
	 */
	endTime: string;
	/**
	 * the location selected for this shift.
	 */
	location?: string;
	originalStartDate: DateTime | null;
	startDate: DateTime;
	endDate: DateTime | null;
	id: number | string;
	status: SingleRecurringShiftStatus;
	/**
	 * only used for weekly shifts that repeat each week
	 */
	isWeeklyShiftThatRepeatsEachWeek?: boolean;
	/**
	 * The last time the user interacted with the UI for this shift (used for determining which shift to focus on after re-renders)
	 */
	lastUIInteraction: DateTime | null;
	jobId?: string;
	/** Present only on merged display rows. Maps weekday → original DB row meta, used by `expandShiftToOps` to decompose back into per-row ops. */
	originalRowsByWeekday?: Record<string, MergedRowMeta>;
};
export type WeeklyShifts = Array<SingleRecurringShift>;

export type RecurringShiftMasterScheduleStateCreateMode = {
	mode: RecurringShiftMasterScheduleType.CREATE;
	selectedNurseId: number | null;
	/**
	 * Hold both shifts that have a recurrence interval of 1 (repeat each week) and shifts that have a recurrence interval = department.master_schedule_weeks
	 *
	 * Inside weeksByIndexes - Each index represents a week in the recurrence pattern -> 0 = week 1, 1 = week 2, etc.
	 * Each week contains an array of recurring shifts for that week
	 */
	weeklyShifts: {
		weeksByIndexes: Array<WeeklyShifts>;
		shiftsThatRepeatEachWeek: WeeklyShifts;
	};
	existingShiftsToRemove: Array<SingleRecurringShift>;
	otherRecurringShifts: Array<SingleRecurringShift>;
};
export type RecurringShiftMasterScheduleStateEditMode = Omit<RecurringShiftMasterScheduleStateCreateMode, "mode"> & {
	mode: RecurringShiftMasterScheduleType.EDIT;
};

/**
 * One pre-filled shift row, scoped to ONE week of the schedule template.
 *
 * This is the shape a caller uses to open the panel with a proposed schedule already on screen —
 * the Hiring Module's suggested hires are the first, and it is deliberately expressed in the panel's
 * OWN model rather than in the caller's: a template week index plus the weekdays inside that week.
 * Because the week is per entry, a pattern that differs between week 1 and week 2 (Mon/Wed then
 * Mon/Fri) survives as two entries instead of collapsing into a single unioned Mon/Wed/Fri — which
 * is exactly what a `Weekly`-with-interval-1 recurrence cannot express.
 *
 * Each entry becomes one `SingleRecurringShift` with status NEW, placed in that week's column with
 * the department's own template interval (`master_schedule_weeks`), so the panel's biweekly-phase
 * math and its save path treat it identically to a row the user added with `+ Add Shifts`.
 */
/**
 * The times on a prefilled row: BOTH or NEITHER, enforced by the compiler rather than by a comment.
 *
 * Two independent optionals let `{ startTime: "07:00" }` compile with no end time, and the panel would
 * then fill in its own default end — producing a shift the caller never asked for, on a real schedule,
 * with nothing to flag it. Omitting both is a real and different intent: take the panel's new-shift
 * defaults (clicked location → the staff suggestion → the house default), i.e. exactly what `+ Add
 * Shifts` would have given.
 */
type PrefillTimes = { startTime: string; endTime: string } | { startTime?: never; endTime?: never };

export type RecurringShiftWeekPrefill = PrefillTimes & {
	/** 0-based template week index; entries outside `[0, master_schedule_weeks)` are ignored. */
	weekIndex: number;
	/** Lowercase weekday names ("monday"), the vocabulary `WeeklyRecurring.weekdays` already uses. */
	weekdays: string[];
	/**
	 * Job to pre-select on the row. `null` is the caller saying "no job on purpose" — distinct from
	 * omitting the field, which says nothing and leaves the panel's own default in charge.
	 */
	jobId?: string | null;
};

/** A staff member's usual shift: what a new shift row starts with when no location was clicked. */
export type StaffShiftDefaults = { startTime: string; endTime: string; location: string };

export type RecurringShiftMasterScheduleProps = {
	clickedDate: DateTime;
	source: ChangesSource;
	clickedLocation?: DepartmentLocation;
	/**
	 * If true, a new shift will be added on open for the clicked date and location
	 */
	shouldAddNewShiftOnOpen: boolean;
	/**
	 * Will attempt to load existing recurring shifts for the nurse
	 */
	clickedNurseId?: number;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);
	/**
	 * All recurring shifts in the department. Used to load existing recurring shifts for the clicked nurse if clickedNurseId is provided
	 * If it is not provided, but the clickedNurseId is, we will attempt to load recurring shifts for the clickedNurseId by fetching them from the backend
	 */
	recurringShiftsData?: RecurringShift[];
	/**
	 * If provided, will determine which tab to open based on the existing recurring shift's recurrence interval
	 */
	knownClickedRecurringShiftId?: number;
	/**
	 * If true, the popup cannot be hidden (closed only)
	 */
	disableHidePopup?: boolean;
	renderWithoutModal?: boolean;
	onVersionChange?: ((version: "new" | "old") => void) | Dispatch<SetStateAction<"new" | "old">>;
	/**
	 * Restrict the staff search to people who hold this job. Set when the panel opens for a shift whose
	 * job is already decided — a suggested hire, which is headcount for one specific job — so the picker
	 * offers the people who could actually take it rather than the whole roster.
	 *
	 * Omitted everywhere else: the general panel is where any of the department's staff can be scheduled.
	 */
	staffJobIdFilter?: string;
	/**
	 * The staff member's usual shifts, best first. A new shift row starts with the first one when no location was
	 * clicked. Staffing and Profile pass the guess from Staffing's loaded weeks; Master schedule doesn't, because a
	 * clicked cell always gives the location there. Without it, such a row starts at 06:00–18:00 with no location.
	 */
	suggestStaffShiftDefaults?: (staffId: number) => StaffShiftDefaults[];
	/**
	 * Optional seed for a panel opened as a CREATE flow: per-template-week shift rows, pre-filled as
	 * NEW shifts (see `RecurringShiftWeekPrefill`). Additive — omit it and every existing call site
	 * behaves exactly as before.
	 *
	 * Re-applied whenever the state is rebuilt (picking a staff member, discarding on a tab change),
	 * so the proposed schedule survives the one interaction the flow depends on: choosing WHO the
	 * schedule is for. Save stays gated on that choice, as it already is.
	 */
	prefilledWeeks?: RecurringShiftWeekPrefill[];
};

export const medicalDefaultTime = "13:00";

export const generateNewShift = ({
	startDate,
	clickedDate,
	weeksInterval,
	timezone,
	clickedLocation,
	staffShiftDefaultsSuggestion,
	isMedicalDepartment = false,
	jobId,
	consecutive,
}: {
	startDate: DateTime;
	clickedDate?: DateTime;
	weeksInterval: number;
	timezone: string;
	clickedLocation?: DepartmentLocation;
	/**
	 * Suggested times and location based on staff's historical shifts.
	 * If provided, these values will be used instead if and only if clickedLocation is not provided,
	 * if clickedLocation is provided, its values take precedence.
	 */
	staffShiftDefaultsSuggestion?: { startTime: string; endTime: string; location: string };
	isMedicalDepartment?: boolean;
	jobId: string | undefined;
	consecutive: boolean;
}) => {
	// Use staff suggestion if provided, otherwise fall back to clickedLocation or defaults
	const startTime = isMedicalDepartment
		? medicalDefaultTime
		: (clickedLocation?.shift_start.substring(0, 5) ?? staffShiftDefaultsSuggestion?.startTime ?? "06:00");
	const endTime = isMedicalDepartment ? medicalDefaultTime : (clickedLocation?.shift_end.substring(0, 5) ?? staffShiftDefaultsSuggestion?.endTime ?? "18:00");
	const location = clickedLocation?.location ?? staffShiftDefaultsSuggestion?.location ?? "";

	const newShift: SingleRecurringShift = {
		id: uuidv4(),
		recurrenceDetails: consecutive
			? defaultConsecutiveShiftConfig
			: {
					recurringType: "Weekly",
					interval: weeksInterval,
					weekdays: clickedDate ? [getDayNameByIndex(clickedDate.weekday % 7).toLowerCase()] : [],
				},
		startDate: DateTime.max(startDate, DateTime.now().setZone(timezone).startOf("day")),
		endDate: null,
		location,
		/**
		 * "HH:mm" format
		 */
		startTime,
		/**
		 * "HH:mm" format
		 */
		endTime,
		status: SingleRecurringShiftStatus.NEW,
		originalStartDate: null,
		isWeeklyShiftThatRepeatsEachWeek: weeksInterval === 1,
		/**
		 * We set this to now so that the newly created shift is focused after re-renders
		 */
		lastUIInteraction: DateTime.now().setZone(timezone),
		jobId,
	};
	return newShift;
};

/**
 * `prefilledWeeks` → NEW shift rows, split into the panel's two weekly buckets.
 *
 * The whole reason this is per-week: the panel models a two-week template as ONE row per week, each
 * a `Weekly` recurrence whose interval is `master_schedule_weeks` and whose week column is derived
 * from its `startDate`. So "week 1 = Mon/Wed, week 2 = Mon/Fri" is two rows in two columns, and the
 * caller never has to union the days into a single every-week recurrence to fit them in one row.
 *
 * A ONE-week template has no second week to differ from — its only recurrence is "every week" — so
 * those rows go to `shiftsThatRepeatEachWeek`, which is where the panel keeps interval-1 shifts.
 *
 * Pure: no hooks, no state. `clickedDateWeekStart` / `clickedDateWeekIndex` are the caller's anchor,
 * and each row's start date walks FORWARD from it to the next occurrence of its own template week,
 * so the row carries its own biweekly phase exactly as `generateNewShift` intends.
 */
export const buildPrefilledShifts = ({
	prefilledWeeks,
	masterScheduleWeeks,
	clickedDateWeekIndex,
	clickedDateWeekStart,
	timezone,
	clickedLocation,
	staffShiftDefaultsSuggestion,
	isMedicalDepartment,
	defaultJobId,
}: {
	prefilledWeeks: RecurringShiftWeekPrefill[];
	masterScheduleWeeks: number;
	clickedDateWeekIndex: number;
	clickedDateWeekStart: DateTime;
	timezone: string;
	clickedLocation?: DepartmentLocation;
	staffShiftDefaultsSuggestion?: { startTime: string; endTime: string; location: string };
	isMedicalDepartment?: boolean;
	/** The selected staff member's job when they have exactly one — an entry's own `jobId` wins. */
	defaultJobId?: string;
}): { weeksByIndexes: Array<WeeklyShifts>; shiftsThatRepeatEachWeek: WeeklyShifts } => {
	const weeksByIndexes: Array<WeeklyShifts> = Array.from({ length: masterScheduleWeeks }, () => []);
	const shiftsThatRepeatEachWeek: WeeklyShifts = [];
	for (const entry of prefilledWeeks) {
		// A week outside the template window has no column to live in, and a row with no days is the
		// state the panel already blocks saving on — neither is worth materialising.
		if (entry.weekIndex < 0 || entry.weekIndex >= masterScheduleWeeks || entry.weekdays.length === 0) {
			continue;
		}
		const weeksAhead = (((entry.weekIndex - clickedDateWeekIndex) % masterScheduleWeeks) + masterScheduleWeeks) % masterScheduleWeeks;
		const generated = generateNewShift({
			startDate: clickedDateWeekStart.plus({ weeks: weeksAhead }),
			weeksInterval: masterScheduleWeeks,
			timezone,
			clickedLocation,
			staffShiftDefaultsSuggestion,
			isMedicalDepartment,
			// `??` covers both: an omitted job and an explicit `null` both defer to the panel's default.
			jobId: entry.jobId ?? defaultJobId,
			consecutive: false,
		});
		const prefilled: SingleRecurringShift = {
			...generated,
			recurrenceDetails: { recurringType: "Weekly", interval: masterScheduleWeeks, weekdays: entry.weekdays.map((weekday) => weekday.toLowerCase()) },
			// Both or neither: a shift the user is about to save should never carry half a caller's
			// times, and with neither it keeps the same defaults `+ Add Shifts` would have produced.
			...(entry.startTime !== undefined && entry.endTime !== undefined ? { startTime: entry.startTime, endTime: entry.endTime } : {}),
		};
		if (masterScheduleWeeks === 1) {
			shiftsThatRepeatEachWeek.push(prefilled);
		} else {
			weeksByIndexes[entry.weekIndex].push(prefilled);
		}
	}
	return { weeksByIndexes, shiftsThatRepeatEachWeek };
};

export const WeekdaysToIndexMap: { [key: string]: number } = {
	sunday: 0,
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
} as const satisfies { [key: string]: number };

/** How a template is stored: one `recurring_shift` row per weekday, or one row covering several weekdays. */
export type TemplateIdFormat = "perDay" | "wholeWeek";

/** One `recurring_shift` row behind a display line, and the weekdays (lowercase) it covers there. */
export type TemplateIdEntry = { id: number; weekdays: string[] };

export type TemplateIds = {
	/** `null` for a non-Weekly row (Consecutive), where weekday storage doesn't apply. */
	format: TemplateIdFormat | null;
	entries: TemplateIdEntry[];
};

const weekPosition = (weekday: string, weekStartDay: number): number => {
	const index = WeekdaysToIndexMap[weekday];
	if (index === undefined) throw new Error(`getTemplateIdEntries: unknown weekday "${weekday}"`);
	return (index - weekStartDay + DAYS_PER_WEEK) % DAYS_PER_WEEK;
};

/** A NEW row carries a client-side uuid until it is saved, so only a positive number is a DB id. */
const savedTemplateId = (id: SingleRecurringShift["id"]): number | null => (typeof id === "number" && id > 0 ? id : null);

/**
 * The saved `recurring_shift` ids behind one panel line, for support users investigating a schedule.
 *
 * The format is read from the ids, not from the `ScheduleTemplateRuntimeWeekStart` flag: with the
 * flag on, `/recurring_shift/per_day` expands a whole-week row into virtual weekdays that share one
 * id, so the same merged line can hold either format. A merged line reads its ids from
 * `originalRowsByWeekday`, which only holds saved weekdays — a weekday added but not yet saved has
 * no id to show.
 */
export const getTemplateIdEntries = (shift: SingleRecurringShift, weekStartDay: number): TemplateIds => {
	const savedId = savedTemplateId(shift.id);
	const params = shift.recurrenceDetails;
	if (params.recurringType !== "Weekly") {
		return { format: null, entries: savedId === null ? [] : [{ id: savedId, weekdays: [] }] };
	}
	const weekdayIds: Array<{ weekday: string; id: number }> = shift.originalRowsByWeekday
		? Object.entries(shift.originalRowsByWeekday).map(([weekday, meta]) => ({ weekday: weekday.toLowerCase(), id: meta.id }))
		: savedId === null
			? []
			: params.weekdays.map((weekday) => ({ weekday: weekday.toLowerCase(), id: savedId }));
	weekdayIds.sort((first, second) => weekPosition(first.weekday, weekStartDay) - weekPosition(second.weekday, weekStartDay));
	const entriesById = new Map<number, TemplateIdEntry>();
	for (const { weekday, id } of weekdayIds) {
		const entry = entriesById.get(id);
		if (entry) entry.weekdays.push(weekday);
		else entriesById.set(id, { id, weekdays: [weekday] });
	}
	const entries = [...entriesById.values()];
	if (entries.length === 0) return { format: null, entries };
	return { format: entries.length === 1 && weekdayIds.length > 1 ? "wholeWeek" : "perDay", entries };
};

export const selectorsSx = {
	backgroundColor: SURFACE,
	":hover": {
		backgroundColor: NEUTRAL[100],
	},
} as const satisfies SxProps;

export enum RecurringShiftTabsTypes {
	/**
	 * shifts with weekly recurrence with interval of [1, department.master_schedule_weeks]
	 */
	masterScheduleShifts = "Weekly Recurring Shifts",
	otherRecurringShifts = "Other Recurring Shifts",
}

/**
 * Stable storage keys for persisting the active tab in user settings (backend `recurring_shift_default_tab` enum).
 * Kept separate from the enum's display labels so the persisted value isn't coupled to UI copy.
 */
export const RECURRING_SHIFT_TAB_STORAGE_KEYS = {
	[RecurringShiftTabsTypes.masterScheduleShifts]: "master_schedule",
	[RecurringShiftTabsTypes.otherRecurringShifts]: "other_recurring",
} as const satisfies Record<RecurringShiftTabsTypes, string>;

export const getRecurringShiftTabFromStorageKey = (key: string | undefined): RecurringShiftTabsTypes =>
	key === RECURRING_SHIFT_TAB_STORAGE_KEYS[RecurringShiftTabsTypes.otherRecurringShifts]
		? RecurringShiftTabsTypes.otherRecurringShifts
		: RecurringShiftTabsTypes.masterScheduleShifts;

export const getRecurringShiftTabs = ({
	intervalAmount,
	weeklyAmount,
	otherRecurringShiftsAmount,
}: {
	intervalAmount: number;
	weeklyAmount: number;
	otherRecurringShiftsAmount: number;
}): SystemTab<RecurringShiftTabsTypes>[] => [
	{
		label: `${RecurringShiftTabsTypes.masterScheduleShifts} (${intervalAmount + weeklyAmount})`,
		value: RecurringShiftTabsTypes.masterScheduleShifts,
	},
	{
		label: `${RecurringShiftTabsTypes.otherRecurringShifts} (${otherRecurringShiftsAmount})`,
		value: RecurringShiftTabsTypes.otherRecurringShifts,
	},
];

export const getShiftTypeIconSx = (dayShift: boolean) =>
	({
		fontSize: "14px",
		color: dayShift ? SHIFT_DAY : SHIFT_NIGHT_FALLBACK,
	}) as const satisfies SxProps;

export const defaultConsecutiveShiftConfig = {
	recurringType: "ConsecutiveDays",
	working_days_amount: 4,
	rest_days_amount: 2,
	interval: undefined as never,
} as const satisfies ConsecutiveDaysRecurring;
